import { TBooking } from "~/types/booking";
import { baseService } from "../base";
import { PaginationResponse } from "../type";
import { CopyBookingParams, CreateQuickEstimateParams, GetBookingParams } from "./booking.types";

export const bookingService = {
  createBooking: async (payload: any) => {
    const { data } = await baseService.post("/jobtickets", payload);
    return data;
  },
  createQuickEstimate: async (payload: CreateQuickEstimateParams) => {
    const { data } = await baseService.post("/jobtickets/quick-estimate", payload);
    return data;
  },
  updateBooking: async ({ id, ...payload }: any) => {
    const { data } = await baseService.patch(`/jobtickets/${id}`, payload);
    return data;
  },
  updateBookingInvoiceConfig: async (bookingId: string, invoiceConfig: any) => {
    // API call for invoice configuration persistence
    const { data } = await baseService.patch(`/jobtickets/${bookingId}/invoice-config`, {
      invoiceConfig
    });
    return data;
  },
  getEstimateVersions: async (bookingId: string) => {
    // Get version history from new table
    const { data } = await baseService.get(`/jobtickets/${bookingId}/estimate-versions`);
    return data;
  },
  getEstimateVersion: async (versionId: string) => {
    // Get a specific version by ID
    const { data } = await baseService.get(`/jobtickets/estimate-versions/${versionId}`);
    return data;
  },
  deleteEstimateVersion: async (versionId: string) => {
    // Delete an estimate version
    const { data } = await baseService.delete(`/jobtickets/estimate-versions/${versionId}`);
    return data;
  },
  copyBooking: async ({ id, ...body }: CopyBookingParams) => {
    const { data } = await baseService.post(`/jobtickets/copy/${id}`, body);
    return data;
  },
  deleteBooking: async (id: string) => {
    const { data } = await baseService.delete(`/jobtickets/${id}`);
    return data;
  },
  getBookings: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets",
      {
        params: params ?? {
          page: 1,
          pageSize: 50,
        },
      }
    );
    return data;
  },
  getBooking: async (id: string) => {
    const { data } = await baseService.get<TBooking>(`/jobtickets/${id}`);
    return data;
  },
  createInventory: async (payload: any) => {
    const { data } = await baseService.post("/inventories", payload);
    return data;
  },
  updateInventory: async (payload: any) => {
    const { data } = await baseService.put(
      `/inventories/${payload.id}`,
      payload
    );
    return data;
  },
  deleteInventory: async (id: string) => {
    const { data } = await baseService.delete(`/inventories/${id}`);
    return data;
  },

  getUpcomingBookings: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets/upcoming-jobtickets",
      {
        params,
      }
    );
    return data;
  },

  getPastBookings: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets/past-jobtickets",
      {
        params,
      }
    );
    return data;
  },

  getMyBookings: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets/my-bookings",
      {
        params,
      }
    );
    return data;
  },

  // Get bookings for a specific client (admin use)
  getClientBookings: async (clientId: string, params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      `/jobtickets/client/${clientId}/bookings`,
      {
        params,
      }
    );
    return data;
  },

  // New: Get bookings where current client is a service client
  getMyServiceClientBookings: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets/my-service-client-bookings",
      {
        params,
      }
    );
    return data;
  },

  // New: Get all bookings where client is either main client or service client
  getAllMyRelatedBookings: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets/all-my-related-bookings",
      {
        params,
      }
    );
    return data;
  },

  // New: Get upcoming bookings including service client bookings
  getUpcomingBookingsIncludingServices: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets/upcoming-jobtickets",
      {
        params,
      }
    );
    return data;
  },

  // New: Get past bookings including service client bookings
  getPastBookingsIncludingServices: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets/past-jobtickets",
      {
        params,
      }
    );
    return data;
  },
  getJobTicketsByServiceTickets: async (params?: GetBookingParams) => {
    const { data } = await baseService.get<PaginationResponse<TBooking>>(
      "/jobtickets/service-tickets",
      {
        params,
      }
    );
    return data;
  },
};
